import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Hero from '../components/Hero';
import SectionTrailer from '../components/SectionTrailer';
import PartnersGrid from '../components/PartnersGrid';
import ProductsBento from '../components/ProductsBento';
import ServicesCutIn from '../components/ServicesCutIn';
import TrustCore from '../components/TrustCore';
import GrainBackground from '../components/GrainBackground';
// import { CheckCircle } from 'lucide-react';
import { fetchHomeStats, fetchAboutTrailer } from '../lib/sanity';
import { defaultAboutTrailer, defaultHomeStats } from '../lib/defaultContent';
import type { HomeStat, AboutTrailerContent } from '../lib/types';
import { useGrainAnimation } from '../hooks/useGrainAnimation';

const STATS = defaultHomeStats;

const StatBar: React.FC<{ stats: HomeStat[] }> = ({ stats }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
      className="relative z-30 bg-white border-t border-b border-gray-100 shadow-sm"
    >
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-100">
          {(stats as StatDef[]).map((stat, i) => (
            <StatCount key={i} stat={stat} index={i} isInView={isInView} />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

type StatDef = { value: number; display: string; label: string; prefix: string; isDecimal?: boolean };

const StatCount: React.FC<{ stat: StatDef; index: number; isInView: boolean }> = ({
  stat,
  index,
  isInView,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 1600;
    const steps = 50;
    const increment = stat.value / steps;
    const stepDuration = duration / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= stat.value) {
        setCount(stat.value);
        clearInterval(timer);
      } else {
        setCount(stat.isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      }
    }, stepDuration);
    return () => clearInterval(timer);
  }, [isInView, stat.value, stat.isDecimal]);

  const formatted = stat.isDecimal
    ? `${stat.prefix}${count.toFixed(1)}M+`
    : `${count.toLocaleString()}+`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="flex flex-col items-center justify-center py-4 sm:py-5 px-3 sm:px-4 gap-0.5"
    >
      <span className="font-playfair font-bold text-xl sm:text-2xl text-primary-red tracking-tight">
        {isInView ? formatted : `${stat.prefix}0`}
      </span>
      <span className="text-[10px] sm:text-xs text-gray-500 font-inter uppercase tracking-wider text-center">
        {stat.label}
      </span>
    </motion.div>
  );
};

const Home: React.FC = () => {
  const { grainAnimationEnabled, setGrainAnimationEnabled } = useGrainAnimation();
  const [stats, setStats] = useState<HomeStat[]>(STATS);
  const [aboutTrailer, setAboutTrailer] = useState<AboutTrailerContent>(defaultAboutTrailer);

  useEffect(() => {
    // Prioritize fetched data, fallback to defaults only if fetch fails
    fetchHomeStats().then((data) => {
      setStats((data && data.length > 0) ? data : defaultHomeStats);
    });
  }, []);

  useEffect(() => {
    // Prioritize fetched data, fallback to defaults only if fetch fails
    fetchAboutTrailer().then((data) => {
      setAboutTrailer(data || defaultAboutTrailer);
    });
  }, []);

  return (
    <main className="relative">
      {/* Animation Toggle */}
      <div className="mt-16 w-full flex justify-end pr-4 relative z-40">
        <button
          onClick={() => setGrainAnimationEnabled(!grainAnimationEnabled)}
          className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm hover:shadow-md transition-shadow text-xs text-gray-600 hover:text-gray-900"
          aria-label="Toggle grain animation"
        >
          <div className={`w-8 h-4 rounded-full transition-colors ${
            grainAnimationEnabled ? 'bg-primary-red' : 'bg-gray-300'
          } relative`}>
            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
              grainAnimationEnabled ? 'right-0.5' : 'left-0.5'
            }`} />
          </div>
          <span className="font-inter">Animation</span>
        </button>
      </div>

      {grainAnimationEnabled && <GrainBackground />}
      <Hero />
      <StatBar stats={stats} />

      <SectionTrailer
        title={aboutTrailer.title}
        description={aboutTrailer.description}
        ctaText={aboutTrailer.ctaText}
        ctaLink={aboutTrailer.ctaLink}
        mediaType={aboutTrailer.mediaType}
        mediaUrl={aboutTrailer.mediaUrl}
        highlights={aboutTrailer.highlights || defaultAboutTrailer.highlights}
      />

      <ProductsBento />

      <ServicesCutIn />

      <TrustCore />

      <PartnersGrid />
    </main>
  );
};

export default Home;
